import { createLoadable } from 'dreamstate';

import { Optional } from '@vimeo/core/types/generic';

import { queryManagerGctlConfigWithFallback } from '../../../api/base';
import { ECredentialsType, getInteractionRoomInfo } from '../../../api/interaction';
import { EInteractionRoomPrivacy, IInteractionRoomInfo } from '../../../types/interaction_room';
import { getIframeReferrer } from '../../../utils/embed/embed_referrer';
import { inline } from '../../../utils/general/lambda';
import { ELiveSignal } from '../../signals';
import type { InteractionSessionManager } from './InteractionSessionManager';
import { checkAutoAuth, checkPasswordAuth } from './InteractionSessionManager.auth';

/**
 * Load session ID room information and possible auth options.
 *
 * @param manager - InteractionSessionManager manager to perform operation for
 */
export async function loadSessionInfo(manager: InteractionSessionManager): Promise<void> {
  const {
    sessionId,
    sessionType,
    roomDisabledAuthOptions,
    roomUnlistedHash,
    roomPassword,
    roomHashedPassword,
    roomInjectedAuthToken: roomJwtToken,
  } = manager.context;

  const referrer: Optional<string> = getIframeReferrer();

  manager.log.info('Loading room info:', {
    sessionId,
    sessionType,
    roomPassword,
    roomHashedPassword,
    roomUnlistedHash,
    roomJwtToken,
    referrer,
  });

  try {
    const roomInfo: IInteractionRoomInfo = await getInteractionRoomInfo(
      {
        sessionType,
        sessionId,
        gctlConfig: queryManagerGctlConfigWithFallback(manager),
        fields: [
          'room_id',
          'room_type',
          'room_hash',
          'embed_code',
          'settings.auth_options',
          'settings.privacy',
          'metadata.connections.auth',
          'metadata.connections.ingest_status',
          'metadata.connections.credentials_check',
          'metadata.misc',
        ],
      },
      {
        ...(roomUnlistedHash ? { roomUnlistedHash: encodeURIComponent(roomUnlistedHash) } : {}),
        ...(roomPassword ? { roomPassword: encodeURIComponent(roomPassword) } : {}),
        ...(roomHashedPassword
          ? { roomHashedPassword: encodeURIComponent(roomHashedPassword) }
          : {}),
        ...(roomJwtToken ? { roomJwtToken } : {}),
        ...(referrer ? { referrer } : {}),
      },
    );

    if (manager.IS_DISPOSED) {
      return manager.log.warn('Disposed when init finish, skip room');
    }

    // Disable login options restricted by query/prop parameters and do not allow usage in further checks.
    // Affects auto-login and possible logins layout list.
    if (roomDisabledAuthOptions.length) {
      Object.entries(roomInfo.settings.authOptions).forEach(([key, value]) => {
        if (value && roomDisabledAuthOptions.includes(key)) {
          roomInfo.settings.authOptions[key] = false;
        }
      });
    }

    manager.log.info('Accessing interaction room:', sessionId, sessionType, roomInfo);

    manager.connections = {
      auth: roomInfo.metadata.connections.auth,
      credentialsCheck: roomInfo.metadata.connections.credentialsCheck,
    };

    manager.setContext({
      roomInfo: createLoadable(roomInfo, !manager.isAuthAutoSignInDisabled),
    });

    /*
     * If everything is successful and auto login is enabled, check auto-login option.
     */
    const roomCredentialsPromise: Promise<void> = inline(() => {
      if (roomInfo?.settings.privacy !== EInteractionRoomPrivacy.PASSWORD) {
        return checkAutoAuth(manager);
      } else if (roomPassword) {
        return checkPasswordAuth(manager, roomPassword);
      } else if (roomHashedPassword) {
        return checkPasswordAuth(manager, roomHashedPassword, ECredentialsType.HASHED_PASSWORD);
      } else {
        return Promise.resolve();
      }
    });

    await roomCredentialsPromise.finally(() => {
      if (manager.IS_DISPOSED) {
        return manager.log.warn('Disposed when auth check finished');
      }

      manager.setContext(({ roomInfo }) => ({ roomInfo: roomInfo.asReady() }));
    });
  } catch (error) {
    manager.log.error('Failed to load session:', error);
    manager.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });
  }
}
